<template>
  <div class="desktop-qr page-wrap">
    <BaseCard tone="soft" class="desktop-qr__card">
      <div class="desktop-qr__eyebrow">Вход по QR</div>
      <h1 class="desktop-qr__title">Открой приложение на телефоне и отсканируй код</h1>
      <p class="desktop-qr__text">
        После подтверждения на мобильном устройстве этот экран автоматически войдёт в аккаунт.
      </p>

      <div v-if="error" class="desktop-qr__alert desktop-qr__alert--danger">
        {{ error }}
      </div>

      <div v-if="loading" class="desktop-qr__loading">
        Подготавливаю одноразовый QR-код…
      </div>

      <div v-else class="desktop-qr__content">
        <div class="desktop-qr__code-wrap">
          <img v-if="qrDataUrl" :src="qrDataUrl" alt="QR код для входа" class="desktop-qr__code" />
        </div>

        <div class="desktop-qr__meta">
          <div class="desktop-qr__step">
            <span class="desktop-qr__step-num">1</span>
            <span>Открой мобильное приложение и зайди на главную</span>
          </div>

          <div class="desktop-qr__step">
            <span class="desktop-qr__step-num">2</span>
            <span>Нажми «Сканировать QR»</span>
          </div>

          <div class="desktop-qr__step">
            <span class="desktop-qr__step-num">3</span>
            <span>Подтверди вход на этом устройстве</span>
          </div>

          <div class="desktop-qr__chip">
            <BaseIcon name="shield" />
            Код действует ограниченное время и используется один раз
          </div>

          <button class="desktop-qr__btn" type="button" @click="createSession">
            Создать новый QR
          </button>

          <router-link class="desktop-qr__link" to="/login">
            Обычный вход
          </router-link>
        </div>
      </div>
    </BaseCard>
  </div>
</template>

<script>
import QRCode from 'qrcode';
import BaseCard from '../components/ui/BaseCard.vue';
import BaseIcon from '../components/ui/BaseIcon.vue';
import { post, isSuccess, extractMessage } from '../services/api';

export default {
  name: 'DesktopQrLoginView',
  components: {
    BaseCard,
    BaseIcon,
  },
  data() {
    return {
      loading: true,
      error: '',
      session: null,
      qrDataUrl: '',
      pollingTimer: null,
      applyingLogin: false,
    };
  },
  async mounted() {
    if (this.$store.getters['auth/isAuthenticated']) {
      this.$router.replace('/');
      return;
    }

    await this.createSession();
  },
  beforeUnmount() {
    this.stopPolling();
  },
  methods: {
    stopPolling() {
      if (this.pollingTimer) {
        clearInterval(this.pollingTimer);
        this.pollingTimer = null;
      }
    },

    async createSession() {
      this.loading = true;
      this.error = '';
      this.session = null;
      this.qrDataUrl = '';
      this.stopPolling();

      try {
        const result = await post('/user/qr-login/start', {
          pc_label: 'ПК',
        });

        if (!isSuccess(result)) {
          this.error = extractMessage(result, 'Не удалось создать QR-сессию');
          this.loading = false;
          return;
        }

        this.session = result.data || {};
        this.qrDataUrl = await QRCode.toDataURL(this.session.qr_value || '', {
          width: 360,
          margin: 1,
        });

        this.startPolling();
      } catch (error) {
        this.error = 'Ошибка сети';
      } finally {
        this.loading = false;
      }
    },

    startPolling() {
      this.stopPolling();

      const interval = Number(this.session?.poll_interval_ms || 1500);

      this.pollingTimer = setInterval(() => {
        this.pollSession();
      }, interval);

      this.pollSession();
    },

    async pollSession() {
      if (!this.session?.secret_token || this.applyingLogin) return;

      try {
        const result = await post('/user/qr-login/poll', {
          secret_token: this.session.secret_token,
        });

        if (!isSuccess(result)) {
          return;
        }

        const payload = result.data || {};

        if (payload.state === 'expired') {
          this.stopPolling();
          this.error = 'Срок действия QR-кода истёк. Создай новый.';
          return;
        }

        if (payload.state === 'used') {
          this.stopPolling();
          return;
        }

        if (payload.state !== 'confirmed' || !payload.user) {
          return;
        }

        this.applyingLogin = true;
        this.stopPolling();

        await post('/user/qr-login/complete', {
          secret_token: this.session.secret_token,
        });

        this.$store.commit('auth/SET_USER', payload.user);
        this.$store.dispatch('orders/clearOrdersCache');
        this.$store.dispatch('booking/resetState');

        await this.$store.dispatch('clubs/fetchClubs', { force: true });

        this.$router.replace('/');
      } catch (error) {
        this.error = 'Ошибка сети';
      } finally {
        this.applyingLogin = false;
      }
    },
  },
};
</script>

<style scoped>
.desktop-qr {
  min-height: calc(100vh - 40px);
  display: grid;
  place-items: center;
}

.desktop-qr__card {
  width: min(100%, 980px);
  background:
    radial-gradient(circle at 15% 18%, rgba(183, 116, 255, 0.14), transparent 0 24%),
    radial-gradient(circle at 86% 16%, rgba(122, 166, 255, 0.12), transparent 0 24%),
    linear-gradient(180deg, rgba(18, 17, 29, 0.98), rgba(8, 8, 14, 0.98));
}

.desktop-qr__eyebrow {
  color: var(--accent);
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.desktop-qr__title {
  margin: 14px 0 0;
  font-size: clamp(34px, 4vw, 56px);
  line-height: 0.98;
  letter-spacing: -0.05em;
}

.desktop-qr__text {
  margin: 14px 0 0;
  max-width: 720px;
  color: var(--text-soft);
  line-height: 1.65;
}

.desktop-qr__alert {
  margin-top: 18px;
  padding: 14px 16px;
  border-radius: 18px;
  font-weight: 700;
}

.desktop-qr__alert--danger {
  color: #ffe4e8;
  background: rgba(255, 107, 121, 0.12);
  border: 1px solid rgba(255, 107, 121, 0.2);
}

.desktop-qr__loading {
  margin-top: 24px;
  color: var(--text-soft);
  font-size: 18px;
  font-weight: 700;
}

.desktop-qr__content {
  margin-top: 28px;
  display: grid;
  gap: 28px;
  grid-template-columns: minmax(320px, 400px) minmax(0, 1fr);
  align-items: center;
}

.desktop-qr__code-wrap {
  padding: 22px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.28);
}

.desktop-qr__code {
  width: 100%;
  aspect-ratio: 1;
  object-fit: contain;
}

.desktop-qr__meta {
  display: grid;
  gap: 14px;
}

.desktop-qr__step {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.05);
  color: var(--text);
  line-height: 1.55;
}

.desktop-qr__step-num {
  width: 34px;
  height: 34px;
  border-radius: 999px;
  display: grid;
  place-items: center;
  background: rgba(183, 116, 255, 0.14);
  font-weight: 900;
  flex: 0 0 auto;
}

.desktop-qr__chip {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 0 16px;
  border-radius: 16px;
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.04);
}

.desktop-qr__btn,
.desktop-qr__link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 54px;
  border-radius: 18px;
  font-weight: 800;
  text-decoration: none;
}

.desktop-qr__btn {
  border: none;
  color: #fff;
  background: var(--button-gradient);
  box-shadow: var(--button-shadow);
  cursor: pointer;
}

.desktop-qr__link {
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.05);
  color: var(--text);
}

@media (max-width: 900px) {
  .desktop-qr {
    min-height: auto;
    padding-top: 10px;
  }

  .desktop-qr__content {
    grid-template-columns: 1fr;
  }

  .desktop-qr__code-wrap {
    max-width: 380px;
  }
}
</style>